import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';

const Text = styled.p`
  color: ${Type.Color.medium};
  font-size: ${Type.Scale._2};
  margin: 0;
  line-height: 1.5;
`;

const MeasureText = styled.p`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  width: 100%;
  color: ${Type.Color.medium};
  font-size: ${Type.Scale._2};
  margin: 0;
  line-height: 1.5;
`;

function TextTruncate({ text = '', maxLines }: { text: string; maxLines: number }) {
  const [showLess, setShowLess] = useState(true);
  const [isTruncated, setIsTruncated] = useState(false);
  const [truncatedText, setTruncatedText] = useState(text);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const calculateTruncation = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const lineHeight = parseFloat(getComputedStyle(measure).lineHeight);
      const maxHeight = lineHeight * maxLines;

      // Reset content to measure full height
      measure.textContent = text;
      const fullHeight = measure.scrollHeight;

      if (fullHeight > maxHeight) {
        setIsTruncated(true);
        // Binary search to find the right truncation point
        let start = 0;
        let end = text.length;
        let bestTruncated = '';

        while (start <= end) {
          const mid = Math.floor((start + end) / 2);
          // Find the last space before our current position
          const lastSpace = text.lastIndexOf(' ', mid);
          const truncated = lastSpace !== -1 ? text.slice(0, lastSpace) + '...' : text.slice(0, mid) + '...';

          measure.textContent = truncated;

          if (measure.scrollHeight <= maxHeight) {
            bestTruncated = truncated;
            start = mid + 1;
          } else {
            end = mid - 1;
          }
        }
        setTruncatedText(bestTruncated);
      } else {
        setIsTruncated(false);
        setTruncatedText(text);
      }
    };

    calculateTruncation();
    window.addEventListener('resize', calculateTruncation);
    return () => window.removeEventListener('resize', calculateTruncation);
  }, [text, maxLines]);

  return (
    <div ref={containerRef} style={{ position: 'relative', minHeight: Grid._3 }}>
      <MeasureText ref={measureRef} />
      <Text className="collapsable-data">
        {showLess ? truncatedText : text}
        {isTruncated && <br />}
        {isTruncated && (
          <a className="link" onClick={() => setShowLess(!showLess)}>
            {showLess ? 'Show More' : 'Show Less'}
          </a>
        )}
      </Text>
    </div>
  );
}

const TrainingOverview = ({ description, maxLines }: { description: string; maxLines: number }) => {
  return <TextTruncate text={description} maxLines={maxLines} />;
};

export default TrainingOverview;
